import { DeleteItem } from "@/components/DeleteItem";
import { ResourceHeader } from "@/components/ResourceHeader";
import { TabItem } from "@/components/ui/TabNavigation/types";
import { useAuth } from "@/context/auth-context";
import { useUser } from "@/context/user-context";
import { useEditPromptTemplate } from "@/queries";
import { getTemplateFormat } from "@/utils/utils";
import {
  ChartBarIcon,
  PencilIcon as PencilIconOutline,
  TrashIcon,
  ViewListIcon,
} from "@heroicons/react/outline";
import {
  ChartBarIcon as ChartBarIconSolid,
  PencilIcon as PencilIconSolid,
  ViewListIcon as ViewListIconSolid,
} from "@heroicons/react/solid";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { v4 } from "uuid";
import EvaluationHistory from "../EvaluationHistory";
import Breadcrumbs from "./Breadcrumbs";
import EditorContent from "./EditorContent";
import {
  usePromptDeletion,
  usePromptTemplateData,
  useTabNavigation,
} from "./hooks";
import { useEvaluationsTableColumns } from "./hooks/useEvaluationsTableColumns";
import OverviewContent from "./OverviewContent";
import RelatedContentDialog from "./RelatedContentDialog";

const PromptTemplate = () => {
  const { promptId, versionId } = useParams();
  const columns = useEvaluationsTableColumns();
  const { promptVersion, promptRegistry, totalVersions, promptName } =
    usePromptTemplateData(promptId!, versionId);

  const overviewSessionId = useRef(v4());
  const navigate = useNavigate();

  useEffect(() => {
    overviewSessionId.current = v4();
  }, [versionId]);

  const { getCurrentTabIndex, handleTabChange, showVersions, setShowVersions } =
    useTabNavigation(promptId!, versionId!);
  const { isDeleteDialogOpen, setIsDeleteDialogOpen, handleDeletePrompt } =
    usePromptDeletion(promptId!);

  const editPromptTemplate = useEditPromptTemplate();
  const { activeWorkspaceId } = useUser();
  const auth = useAuth();
  const userToken = auth?.userToken!;

  const tabs = useMemo<TabItem[]>(
    () => [
      {
        id: 0,
        name: "Overview",
        outlineIcon: (
          <ViewListIcon className="mr-1.5 h-4 w-4" aria-hidden="true" />
        ),
        solidIcon: (
          <ViewListIconSolid className="mr-1.5 h-4 w-4" aria-hidden="true" />
        ),
        content: (
          <OverviewContent
            promptVersion={promptVersion}
            showVersions={showVersions}
            setShowVersions={setShowVersions}
            totalVersions={totalVersions}
            navigateToPlayground={() =>
              navigate(handleTabChange(1) + overviewSessionId.current)
            }
            getCurrentTabIndex={getCurrentTabIndex}
          />
        ),
        path: handleTabChange(0),
      },
      {
        id: 1,
        name: "Editor",
        outlineIcon: (
          <PencilIconOutline className="mr-1.5 h-4 w-4" aria-hidden="true" />
        ),
        solidIcon: (
          <PencilIconSolid className="mr-1.5 h-4 w-4" aria-hidden="true" />
        ),
        content: (
          <EditorContent
            isLoading={promptVersion.isLoading}
            instanceId={overviewSessionId.current}
            promptVersion={promptVersion.data}
          />
        ),
        path: handleTabChange(1) + overviewSessionId.current,
        className: "relative",
      },
      {
        id: 2,
        name: "Evaluations",
        outlineIcon: (
          <ChartBarIcon className="mr-1.5 h-4 w-4" aria-hidden="true" />
        ),
        solidIcon: (
          <ChartBarIconSolid className="mr-1.5 h-4 w-4" aria-hidden="true" />
        ),
        content: (
          <EvaluationHistory tableProps={{ columns }} showViewButtons={false} />
        ),
        path: handleTabChange(2),
      },
    ],
    [
      columns,
      promptVersion,
      showVersions,
      setShowVersions,
      totalVersions,
      getCurrentTabIndex,
      handleTabChange,
      navigate,
    ],
  );

  const handleTitleEdit = useCallback(
    ({ newTitle }: { newTitle: string }) => {
      if (!promptRegistry.data) return;
      editPromptTemplate.mutate({
        prompt_id: Number(promptId),
        userToken,
        basePromptTemplate: {
          prompt_name: newTitle,
          tags: promptRegistry.data.tags,
          folder_id: promptRegistry.data.folder_id!,
          workspace_id: activeWorkspaceId!,
        },
        activeWorkspaceId: activeWorkspaceId!,
      });
    },
    [
      promptRegistry.data,
      promptId,
      userToken,
      activeWorkspaceId,
      editPromptTemplate,
    ],
  );

  const handleTagsEdit = useCallback(
    (tags: string[]) => {
      if (!promptRegistry.data) return;
      editPromptTemplate.mutate({
        prompt_id: Number(promptId),
        userToken,
        basePromptTemplate: {
          prompt_name: promptRegistry.data.prompt_name,
          tags,
          folder_id: promptRegistry.data.folder_id!,
          workspace_id: activeWorkspaceId!,
        },
        activeWorkspaceId: activeWorkspaceId!,
      });
    },
    [
      promptRegistry.data,
      promptId,
      userToken,
      activeWorkspaceId,
      editPromptTemplate,
    ],
  );

  return (
    <div className="flex h-full flex-col p-1 pb-4">
      <div className="mb-3">
        <Breadcrumbs />
      </div>
      <ResourceHeader
        showVersions={showVersions}
        onToggleVersions={() => setShowVersions(!showVersions)}
        isLoading={promptRegistry.isLoading}
        title={promptName || ""}
        minimal={true}
        onTitleEdit={handleTitleEdit}
        id={promptId!}
        format={
          (promptVersion.data &&
            getTemplateFormat(promptVersion.data.prompt_template)) ||
          "f-string"
        }
        tags={promptRegistry.data?.tags || []}
        onTagsEdit={handleTagsEdit}
        createdAt={promptRegistry.data?.created_at || ""}
        actions={[
          {
            label: "Delete",
            onClick: () => setIsDeleteDialogOpen(true),
            variant: "destructiveLink",
            icon: <TrashIcon className="mr-2 h-4 w-4" />,
          },
        ]}
        tabsProps={{
          tabs: tabs,
          currentTabIndex: getCurrentTabIndex(),
          handleTabChange: handleTabChange,
          actionButtons: (
            <div className="relative bottom-1 flex gap-x-2">
              <RelatedContentDialog />
            </div>
          ),
        }}
      />

      {promptRegistry.data && (
        <DeleteItem
          open={isDeleteDialogOpen}
          setOpen={setIsDeleteDialogOpen}
          setDelete={() => handleDeletePrompt()}
          item={promptRegistry.data?.prompt_name}
          itemType="template"
        />
      )}
    </div>
  );
};

export default PromptTemplate;
